import React, { useState } from 'react';
import { Shield, ChevronDown, ChevronUp, Mail, Phone, MapPin, Clock, User, Car, CreditCard, Lock, Eye, Share2, Download } from 'lucide-react';

const PrivacyPolicy = () => {
  const [openSections, setOpenSections] = useState({});

  const toggleSection = (sectionId) => {
    setOpenSections(prev => ({
      ...prev,
      [sectionId]: !prev[sectionId]
    }));
  };

  const sections = [
    {
      id: 'introduction',
      title: '1. Introduction',
      content: `Welcome to RideHub ("we," "our," or "us"). We are committed to protecting your privacy and ensuring the security of your personal information. This Privacy Policy explains how we collect, use, disclose, and safeguard your information when you use our cab booking services through our mobile application and website.

Please read this Privacy Policy carefully. By accessing or using our services, you consent to the practices described in this policy.`
    },
    {
      id: 'information-collection',
      title: '2. Information We Collect',
      content: `We collect several types of information to provide and improve our services:

**Personal Information:**
- Full name and contact details
- Email address and phone number
- Profile information and preferences
- Payment information and transaction history
- Government-issued identification (for driver partners)

**Location Data:**
- Real-time location during active rides
- Pickup and drop-off locations
- Frequently visited places
- Route information and travel patterns

**Device Information:**
- Device type and operating system
- IP address and mobile network information
- App usage statistics and crash reports
- Cookies and similar technologies`
    },
    {
      id: 'how-we-use',
      title: '3. How We Use Your Information',
      content: `We use the collected information for the following purposes:

**Service Delivery:**
- To facilitate ride bookings and matching with drivers
- To process payments and send ride receipts
- To provide customer support and resolve issues
- To send important service updates and notifications

**Service Improvement:**
- To optimize routes and reduce wait times
- To develop new features and enhance user experience
- To conduct research and analysis for service improvement
- To personalize your experience and recommendations

**Safety and Security:**
- To verify user identities and prevent fraud
- To monitor ride safety and handle emergencies
- To maintain service quality and compliance
- To enforce our terms and conditions`
    },
    {
      id: 'information-sharing',
      title: '4. Information Sharing and Disclosure',
      content: `We may share your information in the following circumstances:

**With Driver Partners:**
- Name and rating for identification
- Pickup location to facilitate the ride
- Contact information for coordination (masked when possible)

**Service Providers:**
- Payment processors for transaction handling
- Cloud storage providers for data security
- Analytics services for performance monitoring
- Customer support platforms for assistance

**Legal Requirements:**
- When required by law or legal process
- To protect our rights, property, or safety
- To investigate fraud or security issues
- To comply with regulatory requirements

We do not sell your personal information to third parties for marketing purposes.`
    },
    {
      id: 'data-security',
      title: '5. Data Security',
      content: `We implement comprehensive security measures to protect your information:

**Technical Safeguards:**
- End-to-end encryption for all data transmissions
- Secure socket layer (SSL) technology for web transactions
- Regular security assessments and penetration testing
- Multi-factor authentication for sensitive operations

**Administrative Measures:**
- Strict access controls and authentication protocols
- Regular employee training on data protection
- Confidentiality agreements with all staff
- Incident response and breach notification procedures

**Physical Security:**
- Secure data centers with 24/7 monitoring
- Access control systems and surveillance
- Environmental controls and backup power
- Regular security audits and compliance checks`
    },
    {
      id: 'data-retention',
      title: '6. Data Retention',
      content: `We retain your personal information only for as long as necessary:

**Ride Information:**
- Ride history and transaction records: 7 years for tax and legal compliance
- Location data during active rides: Until ride completion
- Route and trip information: 2 years for service improvement

**Account Information:**
- User profiles and preferences: Until account deletion
- Payment methods: Until removal or account closure
- Communication records: 3 years for quality assurance

**Legal Requirements:**
- We may retain certain information as required by law
- Data may be kept longer for ongoing legal proceedings
- Anonymized data may be retained for analytics purposes`
    },
    {
      id: 'your-rights',
      title: '7. Your Rights and Choices',
      content: `You have the following rights regarding your personal information:

**Access and Control:**
- Right to access your personal data
- Right to correct inaccurate information
- Right to delete your account and data
- Right to restrict or object to processing

**Privacy Settings:**
- Control location sharing preferences
- Manage notification preferences
- Adjust profile visibility settings
- Choose communication preferences

**Data Portability:**
- Request a copy of your data in machine-readable format
- Transfer your data to another service provider
- Download your ride history and information

To exercise these rights, contact us through the app settings or our support channels.`
    },
    {
      id: 'cookies',
      title: '8. Cookies and Tracking Technologies',
      content: `We use cookies and similar technologies to enhance your experience:

**Essential Cookies:**
- Session management and user authentication
- Security and fraud prevention
- Load balancing and performance optimization

**Analytical Cookies:**
- Usage patterns and service improvement
- Feature performance and user behavior
- Service optimization and bug tracking

**Preferences:**
- You can manage cookie preferences through your browser settings
- Some features may not function properly without essential cookies
- We honor "Do Not Track" signals when technically feasible`
    },
    {
      id: 'third-party',
      title: '9. Third-Party Services',
      content: `Our services may integrate with third-party platforms:

**Payment Processors:**
- We use secure payment gateways for transactions
- Payment information is handled by PCI-compliant providers
- We do not store complete payment card details

**Map and Location Services:**
- Integration with mapping services for navigation
- Location data processing for route optimization
- Real-time traffic and route information

**Social Media:**
- Optional integration with social platforms
- Sharing features for referrals and promotions
- Social login capabilities for convenience

All third-party services are bound by strict data protection agreements.`
    },
    {
      id: 'international',
      title: '10. International Data Transfers',
      content: `Your information may be transferred internationally:

**Global Operations:**
- We operate in multiple countries and regions
- Data may be processed in different jurisdictions
- All transfers comply with applicable data protection laws

**Safeguards:**
- Standard contractual clauses for data protection
- Adequacy decisions where applicable
- Binding corporate rules for internal transfers
- Regular compliance monitoring and audits

We ensure that all international data transfers maintain the same level of protection as required by your home country's laws.`
    },
    {
      id: 'children',
      title: '11. Children\'s Privacy',
      content: `Our services are not directed to children under the age of 16:

**Age Restrictions:**
- We do not knowingly collect information from children under 16
- Users must be at least 16 years old to create an account
- Riders under 18 must have parental consent for ride bookings

**Parental Controls:**
- Parents can request deletion of minor's information
- Family profiles with parental supervision features
- Educational resources for safe transportation

If we discover we have collected information from a child under 16, we will promptly delete it.`
    },
    {
      id: 'updates',
      title: '12. Policy Updates',
      content: `We may update this Privacy Policy periodically:

**Notification:**
- We will notify you of significant changes through the app
- Email notifications for major policy revisions
- In-app banners and prompts for important updates

**Review Process:**
- Regular policy reviews to ensure compliance
- Updates based on user feedback and legal requirements
- Version history available upon request

**Acceptance:**
- Continued use of our services after changes constitutes acceptance
- You can review the current policy anytime in the app
- Previous versions are archived for reference`
    },
    {
      id: 'contact',
      title: '13. Contact Information',
      content: `For privacy-related questions or concerns:

**Data Protection Officer:**
Email: myjourneycabs@gmail.com
Phone: +91 9728115781

**General Support:**
Email: myjourneycabs@gmail.com
Phone: +91 9728115781

**Mailing Address:**
Delhi | Bangalore | Bhubaneswar | Mumbai | Chennai

We respond to all privacy inquiries within 48 hours.`
    }
  ];

  const lastUpdated = "January 15, 2024";

  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-50 to-blue-50/30">
      {/* Header */}
      <div className="bg-white border-b border-gray-200">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-6">
          <div className="flex items-center justify-between">
            <div className="flex items-center space-x-4">
              <div className="w-12 h-12 bg-gradient-to-br from-blue-600 to-indigo-700 rounded-2xl flex items-center justify-center">
                <Shield className="w-6 h-6 text-white" />
              </div>
              <div>
                <h1 className="text-2xl font-bold text-gray-900">Privacy Policy</h1>
                <p className="text-gray-600">Last updated: {lastUpdated}</p>
              </div>
            </div>
            <button className="hidden md:flex items-center space-x-2 bg-gray-100 hover:bg-gray-200 px-4 py-2 rounded-lg transition-colors">
              <Download className="w-4 h-4" />
              <span className="font-medium">Download PDF</span>
            </button>
          </div>
        </div>
      </div>

      <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <div className="grid lg:grid-cols-4 gap-8">
          {/* Sidebar Navigation */}
          <div className="lg:col-span-1">
            <div className="bg-white rounded-2xl shadow-sm border border-gray-200 p-6 sticky top-8">
              <h3 className="font-bold text-gray-900 mb-4">Quick Navigation</h3>
              <nav className="space-y-2">
                {sections.map((section) => (
                  <button
                    key={section.id}
                    onClick={() => {
                      document.getElementById(section.id)?.scrollIntoView({ behavior: 'smooth' });
                    }}
                    className="block w-full text-left px-3 py-2 text-sm text-gray-600 hover:text-blue-600 hover:bg-blue-50 rounded-lg transition-colors"
                  >
                    {section.title}
                  </button>
                ))}
              </nav>

              <div className="mt-8 p-4 bg-blue-50 rounded-lg border border-blue-200">
                <div className="flex items-center space-x-3 mb-3">
                  <Shield className="w-5 h-5 text-blue-600" />
                  <h4 className="font-semibold text-blue-900">Your Privacy Matters</h4>
                </div>
                <p className="text-sm text-blue-700 mb-3">
                  We're committed to protecting your personal information and being transparent about our practices.
                </p>
                <div className="flex items-center space-x-4 text-xs text-blue-600">
                  <div className="flex items-center space-x-1">
                    <Lock className="w-3 h-3" />
                    <span>Encrypted</span>
                  </div>
                  <div className="flex items-center space-x-1">
                    <Eye className="w-3 h-3" />
                    <span>Transparent</span>
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* Main Content */}
          <div className="lg:col-span-3">
            <div className="bg-white rounded-2xl shadow-sm border border-gray-200 overflow-hidden">
              {/* Summary Card */}
              <div className="bg-gradient-to-r from-blue-600 to-indigo-700 text-white p-8">
                <div className="flex items-center space-x-4 mb-4">
                  <div className="w-16 h-16 bg-white/20 rounded-2xl flex items-center justify-center backdrop-blur-sm">
                    <Shield className="w-8 h-8" />
                  </div>
                  <div>
                    <h2 className="text-2xl font-bold">Protecting Your Privacy</h2>
                    <p className="text-blue-100">Transparent data practices for your peace of mind</p>
                  </div>
                </div>
                
                <div className="grid md:grid-cols-3 gap-6 mt-6">
                  <div className="text-center">
                    <div className="w-12 h-12 bg-white/20 rounded-2xl flex items-center justify-center mx-auto mb-3 backdrop-blur-sm">
                      <Lock className="w-6 h-6" />
                    </div>
                    <h3 className="font-bold text-lg">Encrypted Data</h3>
                    <p className="text-blue-100 text-sm">End-to-end encryption for all your information</p>
                  </div>
                  <div className="text-center">
                    <div className="w-12 h-12 bg-white/20 rounded-2xl flex items-center justify-center mx-auto mb-3 backdrop-blur-sm">
                      <User className="w-6 h-6" />
                    </div>
                    <h3 className="font-bold text-lg">Your Control</h3>
                    <p className="text-blue-100 text-sm">Manage your privacy settings anytime</p>
                  </div>
                  <div className="text-center">
                    <div className="w-12 h-12 bg-white/20 rounded-2xl flex items-center justify-center mx-auto mb-3 backdrop-blur-sm">
                      <Eye className="w-6 h-6" />
                    </div>
                    <h3 className="font-bold text-lg">Transparency</h3>
                    <p className="text-blue-100 text-sm">Clear explanations of how we use your data</p>
                  </div>
                </div>
              </div>

              {/* Policy Sections */}
              <div className="p-8">
                <div className="prose prose-lg max-w-none">
                  <div className="mb-8 p-6 bg-amber-50 border border-amber-200 rounded-2xl">
                    <h3 className="text-amber-900 font-bold mb-2">Important Notice</h3>
                    <p className="text-amber-800 text-sm">
                      This Privacy Policy applies to all RideHub services, including our mobile application, website, and any related services. By using our services, you agree to the collection and use of information in accordance with this policy.
                    </p>
                  </div>

                  {sections.map((section) => (
                    <div key={section.id} id={section.id} className="mb-8 last:mb-0">
                      <button
                        onClick={() => toggleSection(section.id)}
                        className="flex items-center justify-between w-full text-left py-4 border-b border-gray-200 hover:bg-gray-50 -mx-4 px-4 rounded-lg transition-colors"
                      >
                        <h3 className="text-xl font-bold text-gray-900">{section.title}</h3>
                        {openSections[section.id] ? (
                          <ChevronUp className="w-5 h-5 text-gray-500" />
                        ) : (
                          <ChevronDown className="w-5 h-5 text-gray-500" />
                        )}
                      </button>
                      
                      <div className={`mt-4 text-gray-700 leading-relaxed ${openSections[section.id] ? 'block' : 'hidden'}`}>
                        {section.content.split('\n\n').map((paragraph, index) => (
                          <p key={index} className="mb-4 last:mb-0">
                            {paragraph.split('**').map((text, i) => 
                              i % 2 === 1 ? (
                                <strong key={i} className="text-gray-900">{text}</strong>
                              ) : (
                                text
                              )
                            )}
                          </p>
                        ))}
                      </div>
                    </div>
                  ))}

                  {/* Contact Card */}
                  <div className="mt-12 p-6 bg-gray-50 rounded-2xl border border-gray-200">
                    <h3 className="font-bold text-gray-900 mb-4">Need Help with Privacy?</h3>
                    <div className="grid md:grid-cols-2 gap-6">
                      <div className="flex items-center space-x-3">
                        <div className="w-12 h-12 bg-blue-100 rounded-2xl flex items-center justify-center">
                          <Mail className="w-6 h-6 text-blue-600" />
                        </div>
                        <div>
                          <p className="font-semibold text-gray-900">Email Support</p>
                          <p className="text-sm text-gray-600">myjourneycabs@gmail.com</p>
                        </div>
                      </div>
                      <div className="flex items-center space-x-3">
                        <div className="w-12 h-12 bg-green-100 rounded-2xl flex items-center justify-center">
                          <Phone className="w-6 h-6 text-green-600" />
                        </div>
                        <div>
                          <p className="font-semibold text-gray-900">Phone Support</p>
                          <p className="text-sm text-gray-600">+91 9728115781</p>
                        </div>
                      </div>
                    </div>
                  </div>

                  {/* Quick Actions */}
                  <div className="mt-8 flex flex-wrap gap-4">
                    <button className="flex items-center space-x-2 bg-blue-600 text-white px-6 py-3 rounded-xl font-semibold hover:bg-blue-700 transition-colors">
                      <Download className="w-4 h-4" />
                      <span>Download Policy</span>
                    </button>
                    <button className="flex items-center space-x-2 bg-gray-100 text-gray-700 px-6 py-3 rounded-xl font-semibold hover:bg-gray-200 transition-colors">
                      <Share2 className="w-4 h-4" />
                      <span>Share Policy</span>
                    </button>
                  </div>
                </div>
              </div>
            </div>

            {/* Additional Resources */}
            <div className="grid md:grid-cols-3 gap-6 mt-8">
              <div className="bg-white p-6 rounded-2xl shadow-sm border border-gray-200">
                <div className="w-12 h-12 bg-green-100 rounded-2xl flex items-center justify-center mb-4">
                  <Lock className="w-6 h-6 text-green-600" />
                </div>
                <h4 className="font-bold text-gray-900 mb-2">Security Practices</h4>
                <p className="text-gray-600 text-sm">
                  Learn about our comprehensive security measures and encryption standards.
                </p>
              </div>
              <div className="bg-white p-6 rounded-2xl shadow-sm border border-gray-200">
                <div className="w-12 h-12 bg-purple-100 rounded-2xl flex items-center justify-center mb-4">
                  <User className="w-6 h-6 text-purple-600" />
                </div>
                <h4 className="font-bold text-gray-900 mb-2">Your Rights</h4>
                <p className="text-gray-600 text-sm">
                  Understand your data protection rights and how to exercise them.
                </p>
              </div>
              <div className="bg-white p-6 rounded-2xl shadow-sm border border-gray-200">
                <div className="w-12 h-12 bg-amber-100 rounded-2xl flex items-center justify-center mb-4">
                  <Clock className="w-6 h-6 text-amber-600" />
                </div>
                <h4 className="font-bold text-gray-900 mb-2">Update History</h4>
                <p className="text-gray-600 text-sm">
                  Review previous versions and changes to our privacy policy.
                </p>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default PrivacyPolicy;